import React, { useState, useCallback, useEffect, useRef } from "react";
import Modal from "react-modal";
import "../common.css";
import "./BrandSettingAdd.css";
import config from "@config";
import utilityFunctions from "@store/utilityFunctions";
import { useToast } from "@components/Toast/ToastContext";
import Select from "react-select";
import axios from "axios";
import {
  MdBusiness,
  MdCloudUpload,
  MdClose,
  MdZoomIn,
  MdImage,
  MdInfoOutline,
} from "react-icons/md";
import apiEndpoints from "@store/apiEndpoints";
import InfoModal from "./BrandSetup/InfoModal";

Modal.setAppElement("body");

const countryOptions = [
  { value: "AU", label: "Australia" },
  { value: "NZ", label: "New Zealand" },
];

const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 38,
    borderRadius: 8,
    borderColor: state.selectProps.hasError ? "#dc3545" : "#d1d5db",
    boxShadow: state.isFocused
      ? state.selectProps.hasError
        ? "0 0 0 3px rgba(220,53,69,0.1)"
        : "0 0 0 3px rgba(99,102,241,0.1)"
      : "none",
    "&:hover": {
      borderColor: state.selectProps.hasError ? "#dc3545" : "#9ca3af",
    },
  }),
  placeholder: (base) => ({ ...base, color: "#9ca3af" }),
};

const BrandSettingAdd = ({ closeModal, retrieveBrands, selectedBrand, onBrandCreated }) => {
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [countryValue, setCountryValue] = useState(
    selectedBrand
      ? countryOptions.find((c) => c.value === selectedBrand.country)
      : countryOptions.find((c) => c.value === config.country),
  );
  const [name, setName] = useState(selectedBrand?.name || "");
  const [tag, setTag] = useState(selectedBrand?.tag || "");
  const [sku, setSku] = useState(selectedBrand?.sku_code || "");
  const [hasNoDevices, setHasNoDevices] = useState(
    !selectedBrand ? true : null,
  );
  const [isActive, setIsActive] = useState(
    !selectedBrand ? false : (selectedBrand?.is_active ?? true),
  );
  const [logo, setLogo] = useState(null);
  const [logoPreview, setLogoPreview] = useState(selectedBrand?.logo || "");
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(selectedBrand?.image || "");
  const [errors, setErrors] = useState({});
  const [dragOver, setDragOver] = useState({});
  const [previewData, setPreviewData] = useState(null);
  const [infoModalData, setInfoModalData] = useState(null);

  const logoRef = useRef(null);
  const imageRef = useRef(null);

  useEffect(() => {
    if (!selectedBrand?.id) return;
    const userInfoData =
      localStorage?.getItem("userInfo") ||
      utilityFunctions?.getCookie("userInfo");
    const token =
      (userInfoData && JSON?.parse(userInfoData)?.access_token) || "";
    axios
      .get(
        `${apiEndpoints.device.list()}?brandId=${selectedBrand.id}&page=1&limit=1`,
        { headers: { Authorization: "Bearer " + token } },
      )
      .then((res) => {
        setHasNoDevices(res.data?.count === 0);
        if (res.data?.count === 0) {
          setIsActive(false);
        }
      })
      .catch(() => {
        setHasNoDevices(true);
        setIsActive(false);
      });
  }, [selectedBrand?.id]);

  const handleFileChange = useCallback((file, type) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      const setPreview = {
        logo: setLogoPreview,
        image: setImagePreview,
      };
      const setFile = { logo: setLogo, image: setImage };
      setFile[type](file);
      setPreview[type](reader.result);
      setErrors((prev) => ({ ...prev, [type]: null }));
    };
    reader.readAsDataURL(file);
  }, []);

  const handleInputFileChange = (e, type) => {
    const file = e.target.files[0];
    handleFileChange(file, type);
  };

  const handleDrag = (type, isOver) => {
    setDragOver((prev) => ({ ...prev, [type]: isOver }));
  };

  const handleDrop = (e, type) => {
    e.preventDefault();
    setDragOver((prev) => ({ ...prev, [type]: false }));
    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith("image/")) {
      handleFileChange(file, type);
    }
  };

  const removeFile = (type) => {
    const reset = {
      logo: () => {
        setLogo(null);
        setLogoPreview("");
        if (logoRef.current) logoRef.current.value = "";
      },
      image: () => {
        setImage(null);
        setImagePreview("");
        if (imageRef.current) imageRef.current.value = "";
      },
    };
    reset[type]();
  };

  const addBrand = (payload) => {
    const userInfoData =
      localStorage?.getItem("userInfo") ||
      utilityFunctions?.getCookie("userInfo");
    const token =
      (userInfoData && JSON?.parse(userInfoData)?.access_token) || "";
    let options = {
      headers: {
        Authorization: "Bearer " + token,
        "Content-Type": "multipart/form-data",
      },
    };
    return new Promise((resolve) => {
      axios
        .post(`${config.backendServer}brand/`, payload, options)
        .then((res) => resolve(res))
        .catch((err) => resolve(err));
    });
  };

  const updateBrand = (payload) => {
    const userInfoData =
      localStorage?.getItem("userInfo") ||
      utilityFunctions?.getCookie("userInfo");
    const token =
      (userInfoData && JSON?.parse(userInfoData)?.access_token) || "";
    let options = {
      headers: {
        Authorization: "Bearer " + token,
        "Content-Type": "multipart/form-data",
      },
    };
    return new Promise((resolve) => {
      axios
        .patch(
          `${config.backendServer}brand/${selectedBrand.id}/`,
          payload,
          options,
        )
        .then((res) => resolve(res))
        .catch((err) => resolve(err));
    });
  };

  const handleSubmit = async (event) => {
    setLoading(true);
    event.preventDefault();
    setErrors({});

    const newErrors = {};
    if (!countryValue) {
      newErrors.country = "Please select a country";
    }

    if (!name.trim()) {
      newErrors.name = "Please enter a brand name";
    }

    if (!sku.trim()) {
      newErrors.sku = "Please enter a SKU prefix";
    }

    if (!logo && !selectedBrand?.logo) {
      newErrors.logo = "Please upload a logo";
    }

    if (!image && !selectedBrand?.image) {
      newErrors.image = "Please upload a featured image";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setLoading(false);
      return;
    }

    let formData = new FormData();
    formData.append("country", countryValue.value);
    formData.append("name", name.trim());
    formData.append("tag", tag.trim());
    formData.append("sku_code", sku.trim());
    formData.append("is_active", isActive);

    if (logo) formData.append("logo", logo);
    if (image) formData.append("image", image);

    const result = selectedBrand
      ? await updateBrand(formData)
      : await addBrand(formData);

    if (result.status === 200 || result.status === 201) {
      showToast(
        selectedBrand
          ? "Brand updated successfully"
          : "Brand added successfully",
        "success",
      );
      if (!selectedBrand && result.data?.id && onBrandCreated) {
        onBrandCreated(result.data.id);
      } else {
        closeModal();
        retrieveBrands();
      }
    } else {
      const errorMessage =
        result?.response?.data?.message ||
        result?.response?.data?.detail ||
        "An error occurred";
      showToast(errorMessage, "error");
      setLoading(false);
    }
  };

  const renderUploadZone = (
    id,
    label,
    description,
    preview,
    fileState,
    type,
    inputRef,
    infoContent,
    error,
  ) => {
    const isDragging = dragOver[type];
    const hasPreview = preview || (selectedBrand && !fileState);

    return (
      <div className="brand-upload-wrapper">
        <div className="brand-upload-label-row">
          <label className="brand-upload-label-text">
            {label} <span className="brand-required">*</span>
            {description && (
              <span className="brand-upload-label-desc">
                — {description}
              </span>
            )}
          </label>
          {infoContent && (
            <button
              type="button"
              className="brand-info-btn"
              title="Learn more"
              onClick={(e) => {
                e.stopPropagation();
                setInfoModalData(infoContent);
              }}
            >
              <MdInfoOutline size={15} />
            </button>
          )}
        </div>

        {hasPreview ? (
          <div className="brand-preview-card">
            <div
              className="brand-preview-thumb"
              onClick={() => {
                const src =
                  preview ||
                  {
                    logo: selectedBrand?.logo,
                    image: selectedBrand?.image,
                  }[type];
                if (src) setPreviewData({ src, label });
              }}
              title="Click to preview"
            >
              <img
                src={
                  preview ||
                  (selectedBrand &&
                    {
                      logo: selectedBrand?.logo,
                      image: selectedBrand?.image,
                    }[type])
                }
                alt={`${label} preview`}
                style={{
                  maxWidth: "100%",
                  maxHeight: "100%",
                  objectFit: "contain",
                }}
              />
              <div className="brand-preview-zoom-overlay">
                <MdZoomIn size={22} color="#fff" />
              </div>
            </div>
            <div className="brand-preview-info">
              <p className="brand-preview-filename">
                {fileState ? fileState.name : "Current image"}
              </p>
              <p className="brand-preview-meta">
                {fileState
                  ? `${(fileState.size / 1024).toFixed(1)} KB`
                  : "Click image to preview"}
              </p>
            </div>
            <div className="brand-preview-actions">
              <label
                htmlFor={id}
                className="brand-btn-replace"
                style={{
                  cursor: loading ? "not-allowed" : "pointer",
                  opacity: loading ? 0.5 : 1,
                }}
              >
                Replace
              </label>
              <button
                type="button"
                onClick={() => removeFile(type)}
                disabled={loading}
                className="brand-btn-remove"
              >
                <MdClose size={14} /> Remove
              </button>
            </div>
            <input
              type="file"
              id={id}
              ref={inputRef}
              onChange={(e) => handleInputFileChange(e, type)}
              accept="image/*"
              disabled={loading}
              style={{ display: "none" }}
            />
          </div>
        ) : (
          <div
            className={`brand-upload-zone${isDragging ? " drag-over" : ""}`}
            onDragOver={(e) => {
              e.preventDefault();
              handleDrag(type, true);
            }}
            onDragLeave={() => handleDrag(type, false)}
            onDrop={(e) => handleDrop(e, type)}
            onClick={() => !loading && document.getElementById(id)?.click()}
          >
            <div className="brand-upload-icon-circle">
              <MdCloudUpload size={20} className="brand-upload-icon" />
            </div>
            <p className="brand-upload-text">
              {isDragging
                ? "Drop image here"
                : "Drag & drop or click to upload"}
            </p>
            <p className="brand-upload-hint">PNG, JPG, or SVG</p>
            <input
              type="file"
              id={id}
              ref={inputRef}
              onChange={(e) => handleInputFileChange(e, type)}
              accept="image/*"
              disabled={loading}
              style={{ display: "none" }}
            />
          </div>
        )}
        {error && <small className="brand-form-error">{error}</small>}
      </div>
    );
  };

  const toggleClasses = ["brand-toggle-wrapper"];
  if (hasNoDevices === true) toggleClasses.push("has-no-devices");
  if (loading) toggleClasses.push("loading");

  return (
    <div className="card brand-card">
      <div className="card-header brand-card-header">
        <div className="brand-header-icon">
          <MdBusiness style={{ fontSize: "1.1rem", color: "#4f46e5" }} />
        </div>
        <div>
          <h6 className="brand-header-title">
            {selectedBrand ? "Edit Brand" : "Add New Brand"}
          </h6>
          <p className="brand-header-subtitle">
            {selectedBrand
              ? "Update brand details and assets"
              : "Configure a new brand profile"}
          </p>
        </div>
        <button
          type="button"
          onClick={closeModal}
          disabled={loading}
          className="brand-header-close-btn"
        >
          <MdClose size={20} />
        </button>
      </div>

      <div className="card-body brand-card-body">
        <form onSubmit={handleSubmit}>
          {/* --- Brand Information Section --- */}
          <div className="brand-section-title">Brand Information</div>

          <div className="brand-form-grid">
            <div className="brand-form-group">
              <label className="brand-form-label">
                Country <span className="brand-required">*</span>
              </label>
              <Select
                options={countryOptions}
                value={countryValue}
                onChange={(val) => {
                  setCountryValue(val);
                  setErrors((prev) => ({ ...prev, country: null }));
                }}
                placeholder="Select Country"
                isDisabled={loading}
                hasError={!!errors.country}
                styles={selectStyles}
              />
              {errors.country && (
                <small className="brand-form-error">{errors.country}</small>
              )}
            </div>

            <div className="brand-form-group">
              <label className="brand-form-label">
                Brand Name <span className="brand-required">*</span>
              </label>
              <input
                type="text"
                className={`form-control brand-form-input${errors.name ? " is-invalid" : ""}`}
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  setErrors((prev) => ({ ...prev, name: null }));
                }}
                placeholder="e.g. dreame"
                disabled={loading}
              />
              {errors.name && (
                <small className="brand-form-error">{errors.name}</small>
              )}
            </div>
          </div>

          <div className="brand-mb-0">
            <label className="brand-form-label-flex">
              Tag
              <span
                title="Short identifier used internally to reference this brand"
                style={{ cursor: "help", display: "inline-flex" }}
              />
            </label>
            <input
              type="text"
              className="form-control brand-form-input"
              value={tag}
              onChange={(e) => setTag(e.target.value)}
              placeholder="Optional tag"
              disabled={loading}
            />
          </div>

          <div className="brand-mb-0 brand-mt-16">
            <label className="brand-form-label-flex">
              SKU Prefix <span className="brand-required">*</span>
            </label>
            <input
              type="text"
              className="form-control brand-form-input"
              value={sku}
              onChange={(e) => setSku(e.target.value)}
              placeholder="eg. DRE-, SS-"
              disabled={loading}
            />
          </div>

          <div className="brand-mt-20">
            <label
              className={toggleClasses.join(" ")}
              onClick={() => {
                if (hasNoDevices === false && !loading) setIsActive((prev) => !prev);
              }}
            >
              <div>
                <span className={`brand-toggle-label${hasNoDevices !== false ? " disabled" : ""}`}>
                  Active / Inactive
                </span>
                <span className={`brand-toggle-desc${hasNoDevices !== false ? " disabled" : ""}`}>
                  {hasNoDevices === null
                    ? "Checking devices..."
                    : hasNoDevices
                      ? "Add devices before activating the brand"
                      : isActive
                        ? "Brand is active and visible"
                        : "Brand is inactive and hidden"}
                </span>
              </div>
              <div
                role="switch"
                aria-checked={isActive}
                tabIndex={hasNoDevices !== false ? -1 : 0}
                onKeyDown={(e) => {
                  if (
                    hasNoDevices === false &&
                    !loading &&
                    (e.key === " " || e.key === "Enter")
                  ) {
                    e.preventDefault();
                    setIsActive((prev) => !prev);
                  }
                }}
                className={`brand-toggle-switch${isActive ? " active" : ""}`}
              >
                <div className="brand-toggle-knob" />
              </div>
            </label>
            {hasNoDevices === true && (
              <small className="brand-toggle-error">
                *Add device before activate the brand
              </small>
            )}
          </div>

          {/* --- Brand Assets Section --- */}
          <div className="brand-section-title brand-mt-24">Brand Assets</div>

          <div className="brand-assets-grid">
            {renderUploadZone(
              "uploadLogo",
              "Logo",
              "Main brand logo",
              logoPreview,
              logo,
              "logo",
              logoRef,
              {
                title: "Logo Usage",
                content:
                  "The logo appears in the navigation bar, brand listings, and ticket headers throughout the platform.",
                image: "/logo_from_brand.png",
              },
              errors.logo,
            )}
          </div>
          <div className="brand-assets-grid">
            <div>
              {renderUploadZone(
                "uploadImage",
                "Featured Image",
                "Full brand hero or banner",
                imagePreview,
                image,
                "image",
                imageRef,
                {
                  title: "Featured Image Usage",
                  content:
                    "The featured image appears on the brand detail page hero banner.",
                  image: "/featured_image_from_brand.png",
                },
                errors.image,
              )}
            </div>
            <div />
          </div>

          {/* --- Footer Actions --- */}
          <div className="brand-footer">
            <button
              type="button"
              className="btn btn-light btn-sm brand-btn-cancel"
              onClick={closeModal}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-outline-primary btn-sm brand-btn-submit"
              disabled={loading}
            >
              {loading ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm"
                    role="status"
                  />
                  Saving...
                </>
              ) : selectedBrand ? (
                "Update"
              ) : (
                "Add Brand"
              )}
            </button>
          </div>
        </form>
      </div>

      <Modal
        isOpen={!!previewData}
        onRequestClose={() => setPreviewData(null)}
        style={{
          overlay: { background: "rgba(0,0,0,0.6)", zIndex: 1100, display: "flex", alignItems: "center", justifyContent: "center" },
          content: { position: "relative", inset: "auto", padding: 0, border: "none", borderRadius: 12, overflow: "hidden", maxWidth: "90vw", background: "transparent" },
        }}
      >
        <div className="brand-modal-inner">
          <div className="brand-modal-header">
            <div className="brand-modal-title">
              <MdImage size={18} color="#4f46e5" />
              <span className="brand-modal-title-text">
                {previewData?.label} Preview
              </span>
            </div>
            <button
              onClick={() => setPreviewData(null)}
              className="brand-modal-close-btn"
            >
              <MdClose size={20} />
            </button>
          </div>
          <div className="brand-modal-image-wrap">
            <img
              src={previewData?.src}
              alt={previewData?.label || "Preview"}
              className="brand-modal-image"
            />
          </div>
        </div>
      </Modal>

      <InfoModal
        isOpen={!!infoModalData}
        onClose={() => setInfoModalData(null)}
        title={infoModalData?.title}
        content={infoModalData?.content}
        image={infoModalData?.image}
      />
    </div>
  );
};

export default BrandSettingAdd;
